import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["request"];
import Raven from 'raven-js';
import { useCallback, useMemo, useRef, useState } from 'react';
import debounce from 'react-utils/debounce';
import { useHttpClient } from '../../client/HttpClientContext';
import { Metrics } from '../../metrics/Metrics';
import { trackAutosaveSuccess, trackAutosaveUndo } from '../../utils/usageTrackingUtils';
import { savePropertyValues } from '../api';
import { useItemRegistry } from './useItemRegistry';
import { useAutosaveWindowEvents } from './useAutosaveWindowEvents';
import { useAutosaveStatus } from './useAutosaveStatus';
import { useOnObjectsUpdated } from './useOnObjectsUpdated';
/**
 * Creates a batch for each object type id. Our BE api can handle multiple objectId updates per
 * objectTypeId. Each batch will correspond to one network request.
 */
const getBatches = items => {
  const batches = items.filter(item => item.data.request.type === 'PROPERTY').reduce((acc, item) => {
    const {
      request,
      onSuccess,
      onFailure,
      onUndo
    } = item.data;
    const {
      objectTypeId,
      objectId,
      updates
    } = request;
    const {
      properties,
      previousProperties
    } = updates;
    if (!acc[objectTypeId]) {
      acc[objectTypeId] = {
        type: 'PROPERTY',
        objectTypeId,
        updates: {},
        previousUpdates: {},
        successCallbacks: [],
        failureCallbacks: [],
        undoCallbacks: []
      };
    }
    if (!acc[objectTypeId].updates[objectId]) {
      acc[objectTypeId].updates[objectId] = {};
    }
    if (!acc[objectTypeId].previousUpdates[objectId]) {
      acc[objectTypeId].previousUpdates[objectId] = {};
    }
    properties.forEach(({
      name,
      value
    }) => {
      acc[objectTypeId].updates[objectId][name] = value;
    });
    previousProperties.forEach(({
      name,
      value
    }) => {
      acc[objectTypeId].previousUpdates[objectId][name] = value;
    });
    acc[objectTypeId].successCallbacks.push(onSuccess);
    acc[objectTypeId].failureCallbacks.push(onFailure);
    acc[objectTypeId].undoCallbacks.push(onUndo);
    return acc;
  }, {});
  return Object.values(batches);
};
export const useAutosaveCore = () => {
  const httpClient = useHttpClient();
  const {
    status,
    statusHandlers
  } = useAutosaveStatus();
  const handlersRef = useRef(new Map());
  const [lastSavedItems, setLastSavedItems] = useState([]);
  const [lastSavedBatches, setLastSavedBatches] = useState([]);
  const itemRegistry = useItemRegistry();
  const inFlightSaveCount = useRef(0);
  const getHasInFlightSaves = useCallback(() => inFlightSaveCount.current > 0, [inFlightSaveCount]);
  useAutosaveWindowEvents(getHasInFlightSaves);
  const onObjectsUpdated = useOnObjectsUpdated();
  const undo = useCallback(() => {
    statusHandlers.undoStarted();
    for (const batch of lastSavedBatches) {
      const {
        objectTypeId,
        previousUpdates,
        undoCallbacks,
        failureCallbacks
      } = batch;
      const previousPropertyNames = Object.values(previousUpdates).flatMap(update => Object.keys(update));
      trackAutosaveUndo({
        type: 'PROPERTY',
        objectTypeId,
        identifiers: previousPropertyNames
      });
      savePropertyValues(objectTypeId, previousUpdates, httpClient).catch(e => {
        failureCallbacks.forEach(callback => callback(e));
      }).then(result => {
        Metrics.counter('autosave-property-undo-success').increment();
        if (result) {
          onObjectsUpdated(result, objectTypeId);
          undoCallbacks.forEach(callback => callback(result));
        }
      }).catch(e => {
        Metrics.counter('autosave-property-undo-failure').increment();
        Raven.captureException(new Error('Autosave undo handler failed'), {
          extra: {
            objectTypeId,
            propertyNames: previousPropertyNames,
            originalError: e
          }
        });
      });
    }
  }, [httpClient, lastSavedBatches, statusHandlers, onObjectsUpdated]);
  const save = useCallback(items => {
    inFlightSaveCount.current++;
    const batches = getBatches(items);
    statusHandlers.savingStarted();
    setLastSavedBatches(batches);
    Promise.allSettled(batches.map(batch => {
      const {
        objectTypeId,
        updates,
        successCallbacks,
        failureCallbacks
      } = batch;
      const propertyNames = Object.values(updates).flatMap(update => Object.keys(update));
      return savePropertyValues(objectTypeId, updates, httpClient).catch(e => {
        failureCallbacks.forEach(callback => callback(e));
      }).then(result => {
        if (result) {
          trackAutosaveSuccess({
            type: 'PROPERTY',
            identifiers: propertyNames,
            objectTypeId
          });
          Metrics.counter('autosave-property-success', {
            propertyCount: String(propertyNames.length)
          }).increment();
          onObjectsUpdated(result, objectTypeId);
          successCallbacks.forEach(callback => callback(result));
          return true;
        }
      }).catch(e => {
        Metrics.counter('autosave-property-onsave-failure').increment();
        Raven.captureException(new Error('Autosave success handler failed'), {
          extra: {
            objectTypeId,
            propertyNames,
            originalError: e
          }
        });
        return true;
      });
    })).then(results => {
      const allSuccess = results.every(result => result.status === 'fulfilled' && result.value);
      if (allSuccess) {
        statusHandlers.savingSucceeded();
      } else {
        statusHandlers.savingFailed();
      }
    }).catch(() => {}).finally(() => {
      setLastSavedItems(items);
      inFlightSaveCount.current--;
    });
  }, [statusHandlers, httpClient, onObjectsUpdated]);
  const saveValidRequests = useCallback(() => {
    const handlers = Array.from(handlersRef.current.values());
    const itemsToProcess = itemRegistry.getAll().filter(item => handlers.every(({
      getIsHandlerForRequest,
      getIsValidRequest
    }) => !getIsHandlerForRequest(item.data.request) || getIsValidRequest(item.data.request)));
    if (itemsToProcess.length > 0) {
      itemRegistry.filter(item => !itemsToProcess.includes(item));
      save(itemsToProcess);
    }
  }, [save, itemRegistry, handlersRef]);
  const cancelRequestsForSource = useCallback(sourceId => {
    if (itemRegistry.hasItem(sourceId)) {
      itemRegistry.remove(sourceId);
    }
  }, [itemRegistry]);
  const clearPendingRequests = useCallback(() => {
    const items = itemRegistry.getAll();
    itemRegistry.clear();
    for (const item of items) {
      item.data.onClear();
    }
    statusHandlers.requestsCleared();
    Metrics.counter('autosave-clear-pending-requests').increment();
  }, [itemRegistry, statusHandlers]);
  const enqueueRequest = useCallback(_ref => {
    let {
        request
      } = _ref,
      callbacks = _objectWithoutPropertiesLoose(_ref, _excluded);
    itemRegistry.add({
      id: request.sourceId,
      timestamp: Date.now(),
      data: Object.assign({}, callbacks, {
        request
      })
    });
  }, [itemRegistry]);
  const registerHandler = useCallback(config => {
    handlersRef.current.set(config.id, config);
    return () => {
      handlersRef.current.delete(config.id);
    };
  }, []);
  const items = itemRegistry.getAll();
  const requests = useMemo(() => items.map(item => item.data.request), [items]);
  const invalidRequests = useMemo(() => {
    const handlers = Array.from(handlersRef.current.values());
    return items.filter(item => handlers.some(({
      getIsHandlerForRequest,
      getIsValidRequest
    }) => getIsHandlerForRequest(item.data.request) && !getIsValidRequest(item.data.request))).map(item => item.data.request);
  }, [items]);
  return {
    cancelRequestsForSource,
    clearPendingRequests,
    enqueueRequest,
    hasRequestForSource: itemRegistry.hasItem,
    invalidRequests,
    lastSavedItems,
    registerHandler,
    requests,
    saveValidRequests: useMemo(() => debounce(saveValidRequests, 25), [saveValidRequests]),
    status,
    undo
  };
};